import React, { useEffect, useMemo, useState } from 'react';
import './_select-icon.upload.scss';
import './_select-icon.upload.dark-mode.scss';
import {
  AutoSizer,
  InfiniteLoader,
  List,
} from 'react-virtualized';
import PropTypes from 'prop-types';
import { chunk } from 'lodash';
import { FileUploader } from 'react-drag-drop-files';
import Alert from '../../Alert';
import Spinner from '../../Loaders/Spinner';
import ImagesRowRender from './ImagesRowRender';
import { servicesCategoryIcons, serviceServicesUploadImage } from '../../../services/endpoints/services';
import { pickDevParams } from '../../../utils/config';
import { ICON_TYPES } from '../constants';
import { getErrorMessage } from '../../../utils/helper';

const SelectIconUpload = ({ categoryId, selectedIcon, extensions, onChange }) => {
  const fileTypes = extensions || ['jpg', 'jpeg', 'png', 'gif', 'svg'];
  const [data, setData] = useState({
    isLoading: true,
    error_message: null,
    scrollToIndex: null,
    pagination: {
      count_items: 1,
      current_page: 1,
      page_size: 100,
      pages: 1,
      total_count: 0,
    },
    icons: [],
  })
  const { icons, pagination, isLoading, error_message, scrollToIndex } = data;
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [form, setForm] = useState({
    isUploading: false,
    error_upload: null,
    file: null,
  })
  const { error_upload, file, isUploading } = form;

  const rowItems = window?.screen?.width > 768 ? 8 : 6;
  const stack = Math.ceil(pagination.page_size / rowItems);
  const allRowCount = Math.ceil(pagination.count_items / rowItems);
  const rowCount = Math.ceil(icons.length / rowItems);

  const images = useMemo(() => chunk(icons, rowItems), [icons, rowItems]);

  const handleChange = (newFile) => {
    setForm((prevState) => ({
      ...prevState,
      error_upload: null,
      file: newFile,
    }))
  };

  const clearForm = () => {
    setForm((prevState) => ({
      ...prevState,
      error_upload: null,
      file: null,
      isUploading: false,
    }))
  };

  const formError = (error) => {
    setForm((prevState) => ({
      ...prevState,
      error_upload: error,
      file: null,
      isUploading: false,
    }))
  };

  const onChangeIcon = (icon) => {
    const { id, url } = icon;
    onChange({
      icon_type: ICON_TYPES.IMAGE,
      id,
      url,
    });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const uploadFile = async (file) => {
    try {
      setForm((prevState) => ({
        ...prevState,
        isUploading: true,
        error_upload: null,
      }))

      const formData = new FormData();
      formData.append('file', file);
      Object.keys(pickDevParams).forEach((param) => {
        const value = pickDevParams[param];
        formData.append(param, value);
      })

      const response = await serviceServicesUploadImage(formData);
      if (response.data) {
        const { id, url } = response.data;
        /* Если похожая иконка уже была загружена, бек вернет ее id */
        const findIcon = icons.find((icon) => icon.id === id);
        if (!findIcon) {
          setData((prevState) => ({
            ...prevState,
            scrollToIndex: 0,
            icons: [response.data, ...prevState.icons],
          }))
        }
        onChange({
          icon_type: ICON_TYPES.IMAGE,
          id,
          url,
        });
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      return Promise.reject(errorMessage);
    } finally {
      setData((prevState) => ({
        ...prevState,
        scrollToIndex: null,
      }))
    }
  }

  useEffect(() => {
    if (file) {
      uploadFile(file)
        .then(() => clearForm())
        .catch((reject) => formError(reject))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file])

  useEffect(() => {
    (async () => {
      try {
        const response = await servicesCategoryIcons({
          category_id: categoryId,
          page: pagination.current_page,
        });
        setData((prevState) => ({
          ...prevState,
          icons: response.data.icons,
          isLoading: false,
          error_message: null,
          pagination: response.data.pagination,
        }))
      } catch (error) {
        const error_message = getErrorMessage(error);
        setData((prevState) => ({
          ...prevState,
          isLoading: false,
          error_message,
        }))
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function isRowLoaded({ index }) {
    return (index - stack) !== rowCount;
  }

  const handleNewPageLoad = async () => {
    setIsNextPageLoading(true);
    const repositories = await servicesCategoryIcons({
      category_id: categoryId,
      page: pagination.current_page + 1,
    });

    setData((prevState) => ({
      ...prevState,
      icons: [...prevState.icons, ...repositories.data.icons],
      pagination: repositories.data.pagination,
    }))
    setIsNextPageLoading(false);
  };

  const loadMoreRows = isNextPageLoading ? () => {} : handleNewPageLoad;

  if (isLoading) {
    return (
      <div className="select-icon__upload-preloader">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="select-icon__upload">
      {error_upload || error_message
        ? (
          <div className="select-icon__upload-alert">
            <Alert text={error_upload || error_message} />
          </div>
        ) : null}
      <div className="select-icon__upload-container">
        {icons.length
          ? (
            <>
              <AutoSizer>
                {({ width, height }) => (
                  <InfiniteLoader
                    /* eslint-disable-next-line react/jsx-no-bind */
                    isRowLoaded={isRowLoaded}
                    loadMoreRows={loadMoreRows}
                    rowCount={allRowCount}
                  >
                    {({ onRowsRendered, registerChild }) => (
                      <List
                        onRowsRendered={onRowsRendered}
                        ref={registerChild}
                        height={height}
                        rowCount={rowCount}
                        images={images}
                        rowItems={rowItems}
                        selectedIcon={selectedIcon}
                        onChange={onChangeIcon}
                        rowHeight={50}
                        rowRenderer={ImagesRowRender}
                        width={width}
                        overscanRowCount={15}
                        scrollToIndex={scrollToIndex}
                      />
                    )}
                  </InfiniteLoader>
                )}
              </AutoSizer>
              {isNextPageLoading && <div className="select-icon__upload-page-loader"><Spinner /></div>}
            </>
          )
          : (
            <div className="select-icon__upload-empty">
              <div>
                <div className="select-icon__upload-icon">
                  <span className="fas fa-file-upload" />
                </div>
                <div className="select-icon__upload-description">Recommended size up to 100x100</div>
                <div className="select-icon__upload-description">Max 5MB per file.</div>
              </div>
            </div>
          )}
      </div>
      <FileUploader
        handleChange={handleChange}
        name="file"
        types={fileTypes}
      >
        <div className="select-icon__upload-actions">
          <button
            disabled={isUploading}
            className="btn btn-block btn-default"
            onClick={(e) => e.preventDefault()}
          >
            {isUploading ? (
              <div className="select-icon__upload-actions-loading">
                <Spinner />
                Uploading...
              </div>
            ) : 'Upload file' }
          </button>
        </div>
      </FileUploader>
    </div>
  );
}

SelectIconUpload.propTypes = {
  onChange: PropTypes.func,
  categoryId: PropTypes.number,
  extensions: PropTypes.array,
  selectedIcon: PropTypes.object,
};
export default SelectIconUpload;
